import { useContext } from 'react';
import { ClientConfig, MyChartRedirectBaseUrl } from '../../clientConfig';
import { INVALID_CONFIG_ERROR_MESSAGE, MYCHART_UNAVAILABLE_ERROR_MESSAGE, MyChartRedirectFallbackMessage, REDIRECT_ERROR } from '../../types/errorConstants';
import { getRedirectUrl } from '../../utils/redirectUtil';
import { ClientConfigContext } from '../clientConfigContext';
import { useBrand } from './useBrand';

type MyChartRedirect = {
    isMyChartUrl: (url: string) => boolean;
    getLandingPage: (ehrSystem: string) => string;
    getSamlSsoPage: (ehrSystem: string) => string;
};

const useMyChartRedirect = (): MyChartRedirect => {
    const { b2cTenantConfig, myChartConfig } = useContext<ClientConfig>(ClientConfigContext);
    const { brand } = useBrand(); // may throw error on bad brand name and no default is set.

    const getFallbackUrl = (message: MyChartRedirectFallbackMessage) => {
        const pipHost = b2cTenantConfig.redirectUri || window.location.origin;
        const separator = pipHost.endsWith('/') ? 'error?' : '/error?';
        const params = new URLSearchParams({
            error: REDIRECT_ERROR,
            message,
        });

        return `${pipHost}${separator}${params.toString()}`;
    };

    /** Is the give URL a MyChart URL?
     *  This can be used to determine if a MyChart redirect is appropriate in cross region scenarios
     **/
    const isMyChartUrl = (url: string): boolean => {
        if (!myChartConfig?.domains) {
            return false;
        }

        const allOrigins = Object.values(myChartConfig.domains).map(({ domain }) => `https://${domain}`);
        return allOrigins.some((origin) => url.startsWith(origin));
    };

    const getBaseURL = (ehrSystem: string, brand: string): MyChartRedirectBaseUrl | undefined => {
        if (!myChartConfig) {
            return undefined;
        }

        const baseUrlResult = myChartConfig?.redirectBaseUrls?.find((page) => page.brand === brand && page.ehrSystem === ehrSystem);
        if (baseUrlResult) {
            return baseUrlResult;
        }
    };

    const getSamlSsoPage = (ehrSystem: string) => {
        if (!myChartConfig) {
            return getFallbackUrl(MYCHART_UNAVAILABLE_ERROR_MESSAGE);
        }

        const baseUrl = getBaseURL(ehrSystem, brand);
        if (baseUrl) {
            return getRedirectUrl({
                getBaseURL: () => baseUrl.samlSsoPage,
                brand,
                ehrSystem,
            });
        }
        return getFallbackUrl(INVALID_CONFIG_ERROR_MESSAGE);
    };

    const getLandingPage = (ehrSystem: string) => {
        if (!myChartConfig) {
            return getFallbackUrl(MYCHART_UNAVAILABLE_ERROR_MESSAGE);
        }

        const baseUrl = getBaseURL(ehrSystem, brand);
        if (baseUrl) {
            return getRedirectUrl({
                getBaseURL: () => baseUrl.landingPage,
                brand,
                ehrSystem,
            });
        }
        return getFallbackUrl(INVALID_CONFIG_ERROR_MESSAGE);
    };

    return { getSamlSsoPage, getLandingPage, isMyChartUrl };
};

export { MyChartRedirect, useMyChartRedirect };
